import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
//mui
import { Box, Button, Typography } from '@mui/material';
//components
import CommentSection from './components/CommentSection';
import ItemImage from './components/ItemImage'
import ItemLabel from './components/ItemLabel';
import CollapsibleDiv from '../CollapsableDiv/CollapsableDiv';
//slices
import { setCheckboxStates, setItemDescription, handleOpenModal, setItemPrice, toggleCommentMode, updateCentralizedShowComments, updateCollapsedStates, handleOpenVideoModal, handleOpenARIModal } from '../../../../store/slices/reportsSlice';
import Checkbox from '../../../../components/Checkbox';
import { PROPERTY_ROLE } from '../../../../constants';
//assets
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommentIcon from '@mui/icons-material/Comment';
import CommentsDisabledIcon from '@mui/icons-material/CommentsDisabled';
import ddArrow from "../../../../assets/icons/dd-arrow.svg";
import AuditoryNotes from './components/AuditoryNotes/AuditoryNotes';


const COMMENT_MODE = {
    EDIT: 'Edit Comment',
    ADD: 'Add Comment'
};

const getBoxStyles = (commentModes, tabView, itemMetadata, item, editExportMode, finishExportMode) => {
    let currentCommentMode = commentModes[item.object_id] || COMMENT_MODE.ADD;
    // If the item has metadata and the description/price has value, set the comment mode to edit
    if (itemMetadata?.[tabView]?.[item.object_id] && ((itemMetadata[tabView][item.object_id]?.description || "") !== "" || (itemMetadata?.[tabView]?.[item.object_id]?.price || "") !== "")) {
        currentCommentMode = COMMENT_MODE.EDIT;
    }
    const isAddMode = currentCommentMode === COMMENT_MODE.ADD && (editExportMode && !finishExportMode);
    const isEditMode = currentCommentMode === COMMENT_MODE.EDIT && (editExportMode && !finishExportMode);
    
    const boxHeight = isAddMode ? '270px' : 'inherit';
    const boxBackgroundColor = isEditMode ? 'rgba(127, 40, 11, 0.02)' : 'transparent';
    
    return { boxHeight, boxBackgroundColor, currentCommentMode };
};

const CentralizedAssetOverview = ({ header, groups = [], assetItems = [], assetItemsIndexes = [], setNotifState, auditoryItems = [] }) => {
    const dispatch = useDispatch();
    const { 
        finishExportMode, checkboxStates, report_id, exportReport, tabView, collapsedStates, zoomOutView,
        previewMode, editExportMode, itemMetadata, commentModes, centralizedShowComments, classifiedMetadata
    } = useSelector((state) => state.reports);


    const handleCheckboxChange = (object_id, event) => {
        dispatch(setCheckboxStates({ object_id, checked: event.target.checked }));
    };

    const handleDescriptionChange = (object_id, newValue) => {
        dispatch(setItemDescription({ object_id, description: newValue }));
    };

    const handlePriceChange = (object_id, newValue) => {
        dispatch(setItemPrice({ object_id, price: newValue }));
    };
    
    const toggleComment = (object_id) => {
        dispatch(toggleCommentMode(object_id));
    };

    // Use useMemo to memorize the previewItems calculation
    const previewItems = useMemo(() => (
        previewMode ? 
            assetItems.map((item) => ({
                ...item,
                description: itemMetadata?.[tabView]?.[item.object_id]?.description || '',
                price: itemMetadata?.[tabView]?.[item.object_id]?.price || ''
            })) 
            : 
            assetItems
    ), [previewMode, assetItems, itemMetadata]);

    // console.log("At AssetOverview, header: ", header, "\npreviewItems: ", previewItems);

    if ((!Array.isArray(previewItems) || previewItems.length === 0) && (auditoryItems || []).length === 0) {
        return null;
    }


    return (
        <CollapsibleDiv
            isOpen={collapsedStates.includes(header)}
            minHeight={56}
            maxHeight={1000}
            style={{ 
                border: '2px solid rgba(12, 18, 34, 0.2)', 
                borderRadius: '8px',
                overflowY: !collapsedStates.includes(header) ? 'hidden' : 'auto',
            }}
        >
            {/* Header */}
            <div className='flex'>
                {(!previewMode && editExportMode) &&
                    <Checkbox
                        checked={previewItems.every((item) => checkboxStates?.[tabView]?.[item.object_id])}
                        onChange={(e) => {
                            previewItems.forEach((item) => handleCheckboxChange(item.object_id, e));
                        }}
                        sx={{ padding: '0', height: '24px', width: '24px', marginRight: '4px' }}
                    />
                }
                <Typography 
                    variant='h6' 
                    sx={{ 
                        fontSize: '16px', 
                        fontWeight: 700, 
                        color: '#0C1222', 
                        marginBottom: '16px' 
                    }}
                >
                    {header} ({previewItems.length + auditoryItems.length})
                </Typography>

                <div className='ml-auto flex'>
                    <div className="flex mr-4">
                        <div 
                            className="flex mr-2 cursor-pointer"
                            style={{
                                padding: '2px 4px',
                            }}
                            onClick={() => dispatch(handleOpenVideoModal({target: header}))}
                        >
                            <PlayCircleIcon sx={{ color: '#3C3EB8', fontSize: '20px' }} />
                        </div>
                        <div 
                            className="flex mr-2"
                            style={{
                                padding: '2px 4px',
                            }}
                        >
                            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '20px' }} />
                            <div
                                className='text-xs font-medium hide-mobile'
                                style={{
                                    color: "#4CAF50",
                                    fontWeight: 700,
                                    padding: '2px 0px 2px 4px',
                                }}
                            >
                                {groups.map(
                                    (gn) => previewItems.filter(
                                        item => item.classifiedCategory === gn && item.class >= 3000
                                    ).length
                                ).filter((count) => count === 0).length}
                            </div>
                        </div>
                        <div 
                            className="flex mr-2"
                            style={{
                                padding: '2px 4px',
                            }}
                        >
                            <WarningIcon sx={{ color: '#FF0000', fontSize: '20px' }} />
                            <div
                                className='text-xs font-medium hide-mobile'
                                style={{
                                    color: '#FF0000',
                                    fontWeight: 700,
                                    padding: '2px 0px 2px 4px',
                                }}
                            >
                                {groups.map(
                                    (gn) => previewItems.filter(
                                        item => item.classifiedCategory === gn && item.class >= 3000
                                    ).length
                                ).filter((count) => count > 0).length}
                            </div>
                        </div>
                        <div 
                            className="flex"
                            style={{
                                padding: '2px 4px',
                            }}
                        >
                            <div style={{fontSize: '20px', lineHeight: '20px', marginLeft: '-4px'}}>🎙️</div>
                            <div
                                className='text-xs font-medium hide-mobile'
                                style={{
                                    color: '#0C1222',
                                    fontWeight: 700,
                                    padding: '2px 0px 2px 4px',
                                }}
                            >
                                {auditoryItems.length}
                            </div>
                        </div>
                    </div>

                    <button 
                        style={{marginTop: '-16px'}}
                        onClick={() => dispatch(updateCollapsedStates({ section: header, collapse: !collapsedStates.includes(header) }))}
                    >
                        <img src={ddArrow} alt="Arrow" style={{ width: '16px', transform: collapsedStates.includes(header) ? 'rotate(180deg)' : '' }} />
                    </button>
                </div>
            </div>

            {auditoryItems.length > 0 && (
                <AuditoryNotes
                    setNotifState={setNotifState}
                    items={auditoryItems}
                    header={`${header}-AUDITORY_NOTES`}
                />
            )}

            {groups.map((groupName, gi) => {
                const groupID = `${header}-${groupName}`;
                const groupItem = {object_id: groupID};
                if ((classifiedMetadata?.[groupID] || {})?.description) {
                    groupItem['description'] = classifiedMetadata[groupID].description;
                }
                if ((classifiedMetadata?.[groupID] || {})?.repair_cost_json) {
                    groupItem['repair_cost_json'] = classifiedMetadata[groupID].repair_cost_json;
                }
                const groupItems = previewItems.map((item, index) => [item, index]).filter(([item, index]) => item.classifiedCategory === groupName);
                const hasDamages = groupItems.some(([item, index]) => item.class >= 3000);

                return (
                    <CollapsibleDiv
                        isOpen={collapsedStates.includes(groupID)}
                        minHeight={64}
                        maxHeight={1000}
                        style={{
                            border: hasDamages ? '2px solid #FF0000' : '1px solid #F2F2F3', 
                            borderRadius: '8px',
                        }}
                    >
                        <div className='flex'>
                            {(!previewMode && editExportMode) &&
                                <Checkbox
                                    checked={groupItems.every(([item,_]) => checkboxStates?.[tabView]?.[item.object_id])}
                                    onChange={(e) => {
                                        groupItems.forEach(([item,_]) => handleCheckboxChange(item.object_id, e));
                                    }}
                                    sx={{ padding: '0', height: '24px', width: '24px', marginRight: '4px' }}
                                />
                            }
                            <Typography 
                                variant='h6' 
                                sx={{ 
                                    fontSize: '16px', 
                                    fontWeight: 700, 
                                    color: '#0C1222', 
                                    marginTop: '2px',
                                    marginBottom: '16px' 
                                }}
                            >
                                {groupName} ({groupItems.length})
                            </Typography>
                            <div className="ml-auto flex">
                                {!previewMode &&
                                    <button 
                                        className="mr-4 cursor-pointer" 
                                        style={{height: 'fit-content'}}
                                        onClick={
                                            () => dispatch(updateCentralizedShowComments({
                                                k: groupID, 
                                                v: centralizedShowComments?.[groupID] === false 
                                            }))
                                        }
                                    >
                                        {centralizedShowComments?.[groupID] === false ?
                                            <CommentIcon sx={{ color: '#3C3EB8', fontSize: '24px' }} /> :
                                            <CommentsDisabledIcon sx={{ color: '#3C3EB8', fontSize: '24px' }} />
                                        }
                                    </button>
                                }
                                <div>
                                    <div 
                                        className="flex"
                                        style={{
                                            border: '1px solid #4F46E51A',
                                            borderRadius: '4px',
                                            padding: '4px 14px',
                                            backgroundColor: '#F2F2F3',
                                        }}
                                    >
                                        {hasDamages ? (
                                            <WarningIcon sx={{ color: '#FF0000', fontSize: '20px' }} />
                                        ) : (
                                            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '20px' }} />
                                        )}
                                        <div
                                            className='text-xs font-medium hide-mobile'
                                            style={{
                                                color: hasDamages ? '#FF0000' : "#4CAF50",
                                                fontWeight: 700,
                                                padding: '2px 0px 2px 4px',
                                            }}
                                        >
                                            {hasDamages ? "Attention Required" : "OK"}
                                        </div>
                                    </div>
                                </div>

                                <div className="ml-4 mt-1">
                                    <button 
                                        onClick={() => dispatch(updateCollapsedStates({ section: groupID, collapse: !collapsedStates.includes(groupID) }))}
                                    >
                                        <img src={ddArrow} alt="Arrow" style={{ width: '16px', transform: collapsedStates.includes(groupID) ? 'rotate(180deg)' : '' }} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div 
                            className="pt-4 pb-8"
                            style={
                                {
                                    transition: 'max-height 1s ease, padding 0.5s ease',
                                    overflow: 'hidden',
                                    ...((centralizedShowComments?.[groupID] !== false) ? {
                                        maxHeight: '2500px'
                                    } : {
                                        transition: 'max-height 0.5s ease, padding 1s ease',
                                        maxHeight: '0px',
                                        padding: '0'
                                    })
                                }
                            }
                        >
                            <div className="flex flex-wrap gap-4">
                                {(previewMode || editExportMode || groupItem?.description) &&
                                    <Box
                                        key={groupID.replaceAll(" ", "_").replaceAll("/", "_")+"_description"}
                                        className={'bg-white rounded-lg shadow-md p-4 flex flex-col'}
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            backgroundColor: 'rgba(14, 40, 180, 0.03)',
                                            maxWidth: '500px',
                                            boxSizing: 'border-box',
                                            ...(editExportMode ? {
                                                width: '500px',
                                                maxWidth: '100%'
                                            } : {})
                                        }}
                                    >
                                        {!previewMode ?
                                            (editExportMode ?
                                                <CommentSection
                                                    currentCommentMode={'Edit Comment'}
                                                    item={groupItem}
                                                    showAutoGen={true}
                                                    handleDescriptionChange={handleDescriptionChange}
                                                    handlePriceChange={handlePriceChange}
                                                    toggleCommentMode={() => toggleComment(groupID)}
                                                /> :
                                                <Typography
                                                    sx={{
                                                        fontWeight: 400,
                                                        fontSize: '14px',
                                                        color: '#0C1222',
                                                        marginBottom: '8px',
                                                    }}
                                                >
                                                    <b>AI Description</b> <br/>
                                                    {groupItem?.description}
                                                </Typography>
                                            )
                                            :
                                            <>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 400,
                                                        color: '#0C1222',
                                                        // opacity: 0.56,
                                                    }}
                                                >
                                                    {itemMetadata?.[tabView]?.[groupID]?.description || ''}
                                                </Typography>
                                                {(!exportReport.currentExportReport?.exported_data?.excludeCost && exportReport.propertyRole === PROPERTY_ROLE.RESIDENT) &&
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            color: '#0C1222',
                                                            marginTop: '8px',
                                                            // opacity: 0.56,
                                                        }}
                                                    >
                                                        Cost: {exportReport.currentExportReport?.exported_data?.currency === 'eur' ? '€' : '$'}{Number((itemMetadata?.[tabView]?.[groupID]?.price && itemMetadata?.[tabView]?.[groupID]?.price !== "") ? itemMetadata[tabView][groupID]?.price : 0).toLocaleString('en-US')}
                                                    </Typography>
                                                }
                                            </>
                                        }
                                    </Box>
                                }
                                {(!previewMode && groupItem?.repair_cost_json) &&
                                    <Box
                                        key={groupID.replaceAll(" ", "_").replaceAll("/", "_")+"_repairs"}
                                        className={'bg-white rounded-lg shadow-md p-4 flex flex-col'}
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            backgroundColor: 'rgba(14, 40, 180, 0.03)',
                                            maxWidth: '500px',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                color: '#0C1222',
                                                marginBottom: '8px',
                                            }}
                                        >
                                            <u>
                                                Est. Repair Cost: {(groupItem?.repair_cost_json?.currency_sign || "$")}{groupItem?.repair_cost_json?.min} - {(groupItem?.repair_cost_json?.currency_sign || "$")}{groupItem?.repair_cost_json?.max}
                                            </u> <br/>
                                            {groupItem?.repair_cost_json?.description}
                                        </Typography>
                                    </Box>
                                }
                            </div>
                        </div>

                        {/* Grid layout for items */}
                        <div 
                            className={zoomOutView ?
                                'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' :
                                'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'
                            }
                        >
                            {groupItems.map(([item, index]) => {
                                const { boxHeight, boxBackgroundColor } = getBoxStyles(commentModes, tabView, itemMetadata, item, editExportMode, finishExportMode);

                                return (
                                    <Box
                                        key={item.object_id}
                                        className={'bg-white rounded-lg shadow-md p-4 flex flex-col' + (item.class >= 3000 ? ' border border-red-200' : '')}
                                        sx={{ 
                                            position: 'relative',
                                            overflow: 'hidden',
                                            minWidth: '200px',
                                            // height: boxHeight,
                                            backgroundColor: boxBackgroundColor,
                                            maxWidth: zoomOutView ? '500px' : '300px',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        {item.class >= 3000 && (
                                            <div 
                                                className='red-gradient-bg'
                                                style={{
                                                    position: 'absolute',
                                                    top: '-200px',
                                                    right: '-350px',
                                                    height: '400px',
                                                    width: '700px',
                                                    zIndex: -1,
                                                    background: "linear-gradient(268.33deg, #F9B8D9 17.03%, #FFEBF5 36.31%, #FEF7DF 60.4%, #FFD1E8 85.53%)",
                                                    filter: "blur(150px)",
                                                    opacity: 0.6,
                                                }}
                                            >
                                                <div/>
                                            </div>
                                        )}
                                        {/* Image with click handler to open modal */}
                                        <ItemImage
                                            item={item}
                                            index={assetItemsIndexes[index]}
                                            handleOpenModal={handleOpenModal}
                                            dispatch={dispatch}
                                        />

                                        {/* Label and Icon Row */}
                                        <ItemLabel
                                            report_id={report_id}
                                            item={item}
                                            index={assetItemsIndexes[index]}
                                            previewMode={previewMode}
                                            editExportMode={editExportMode}
                                            finishExportMode={finishExportMode}
                                            checkboxStates={checkboxStates}
                                            tabView={tabView}
                                            handleCheckboxChange={handleCheckboxChange}
                                            dispatch={dispatch}
                                            setNotifState={setNotifState}
                                        />

                                        {/* Item Subject */}
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: '#0C1222',
                                            }}
                                        >
                                            {(editExportMode || previewMode) ? itemMetadata?.[tabView]?.[item.object_id]?.title || item.subject : item.subject}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            {(!editExportMode && !previewMode) &&
                                <Box
                                    key={groupID.replaceAll(" ", "_").replaceAll("/", "_")+"_addNew"}
                                    className={'bg-white rounded-lg shadow-md p-4 flex flex-col'}
                                    sx={{ 
                                        position: 'relative',
                                        overflow: 'hidden',
                                        minWidth: '200px',
                                        // height: '270px',
                                        backgroundColor: 'transparent',
                                        maxWidth: zoomOutView ? '500px' : '300px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <div
                                        className={zoomOutView ?
                                            "w-full h-96 bg-gray-100 rounded-md overflow-hidden mb-2 cursor-pointer" :
                                            "w-full h-32 bg-gray-100 rounded-md overflow-hidden mb-2 cursor-pointer"
                                        }
                                        onClick={() => dispatch(handleOpenARIModal({ room: header }))}
                                    >
                                        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 p-4">
                                            <p 
                                                style={{
                                                    color: "#3C3EB8", 
                                                    fontSize: '64px', 
                                                    fontWeight: 'bold', 
                                                    userSelect: 'none', 
                                                    textAlign: 'center'
                                                }}
                                            >
                                                +
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            marginTop: '8px',
                                            backgroundColor: '#4F46E5',
                                            '&:hover': {
                                                backgroundColor: '#3C3EB8',
                                            }
                                        }}
                                        onClick={() => { dispatch(handleOpenARIModal({ room: header })) }}
                                    >
                                        Add New Item
                                    </Button>
                                </Box>
                            }
                        </div>
                    </CollapsibleDiv>
                );
            })}
        </CollapsibleDiv>
    );
};

export default CentralizedAssetOverview;
